import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  IconButton
} from "@mui/material";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import AppToast from "../../components/AppToast";

const ManageDummyStats = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedStat, setSelectedStat] = useState(null);
  const [editData, setEditData] = useState({ sqft: 0, totalRequests: 0, individualClients: 0 });
  
  const [globalStats, setGlobalStats] = useState(null);
  const [editGlobalModalOpen, setEditGlobalModalOpen] = useState(false);
  const [editGlobalData, setEditGlobalData] = useState({ totalSqft: 0, totalRequests: 0, totalIndividualClients: 0, adminAddedProjects: 0 });

  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${url}/api/dummy-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        // Sort using the same logic
        const sorted = res.data.data.sort((a, b) => {
          const predefinedOrder = [
            "1 BHK", "2 BHK", "2.5 BHK", "3 BHK", "4 BHK", "5 BHK",
            "Studio", "Bungalow", "Shop / Office (Up to 1000 sq ft)", "Shop / Office (1000-5000 sq ft)"
          ];
          const idxA = predefinedOrder.indexOf(a.propertyType);
          const idxB = predefinedOrder.indexOf(b.propertyType);
          if (idxA !== -1 && idxB !== -1) return idxA - idxB;
          if (idxA !== -1) return -1;
          if (idxB !== -1) return 1;
          return a.propertyType.localeCompare(b.propertyType);
        });
        setStats(sorted);
      }

      const globalRes = await axios.get(`${url}/api/dummy-stats/global`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (globalRes.data.success) {
        setGlobalStats(globalRes.data.data);
      }
    } catch (err) {
      console.error("Error fetching dummy stats:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchStats();
  }, [token]);

  const handleEditClick = (stat) => {
    setSelectedStat(stat);
    setEditData({ sqft: stat.sqft || 0, totalRequests: stat.totalRequests || 0, individualClients: stat.individualClients || 0 });
    setEditModalOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const res = await axios.put(
        `${url}/api/dummy-stats/${selectedStat._id}`,
        { sqft: editData.sqft, totalRequests: editData.totalRequests, individualClients: editData.individualClients },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        setToast({
          open: true,
          type: "success",
          title: "Success",
          message: "Dummy stats updated successfully!"
        });
        setEditModalOpen(false);
        fetchStats();
      }
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        title: "Update Failed",
        message: "Failed to update dummy stats."
      });
    } finally {
      setSaving(false);
    }
  };

  const handleGlobalEditClick = () => {
    setEditGlobalData({
      totalSqft: globalStats?.totalSqft || 0,
      totalRequests: globalStats?.totalRequests || 0,
      totalIndividualClients: globalStats?.totalIndividualClients || 0,
      adminAddedProjects: globalStats?.adminAddedProjects || 0
    });
    setEditGlobalModalOpen(true);
  };

  const handleGlobalSave = async () => {
    try {
      setSaving(true);
      const res = await axios.put(
        `${url}/api/dummy-stats/global`,
        editGlobalData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.success) {
        setToast({
          open: true,
          type: "success",
          title: "Success",
          message: "Global dummy stats updated successfully!"
        });
        setEditGlobalModalOpen(false);
        fetchStats();
      }
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        title: "Update Failed",
        message: "Failed to update global dummy stats."
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={() => setToast({ ...toast, open: false })}
      />

      <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, mb: 3 }}>
        Manage Dummy Statistics
      </Typography>

      <Paper elevation={0} sx={{ p: 3, backgroundColor: "#FFF", borderRadius: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}40`, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
            Global Dummy Additions
          </Typography>
          <Button
            variant="outlined"
            onClick={handleGlobalEditClick}
            sx={{
              borderColor: COLORS.PRIMARY,
              color: COLORS.PRIMARY,
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              "&:hover": { backgroundColor: "#f3ecf8", borderColor: COLORS.PRIMARY }
            }}
          >
            Edit Global Stats
          </Button>
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" p={2}><CircularProgress size={24} /></Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 3 }}>
            <Box>
              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT, mb: 1 }}>Total Individual Clients</Typography>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.SECONDARY }}>{globalStats?.totalIndividualClients || 0}</Typography>
            </Box>
            <Box>
              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT, mb: 1 }}>Total Requests</Typography>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.SECONDARY }}>{globalStats?.totalRequests || 0}</Typography>
            </Box>
            <Box>
              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT, mb: 1 }}>Total Sq Ft</Typography>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.SECONDARY }}>{(globalStats?.totalSqft || 0).toLocaleString()}</Typography>
            </Box>
            <Box>
              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT, mb: 1 }}>Admin Added Projects</Typography>
              <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.SECONDARY }}>{globalStats?.adminAddedProjects || 0}</Typography>
            </Box>
          </Box>
        )}
      </Paper>

      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY, mb: 2 }}>
        Stats by Property Plan
      </Typography>
      <Paper elevation={0} sx={{ p: 3, backgroundColor: "#FFF", borderRadius: 2, border: `1px solid ${COLORS.LIGHT_SECONDARY}40` }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ width: '25%', ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                    Property Type
                  </TableCell>
                  <TableCell align="center" sx={{ width: '25%', ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                    Total SqFt
                  </TableCell>
                  <TableCell align="center" sx={{ width: '20%', ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                    Total Requests
                  </TableCell>
                  <TableCell align="center" sx={{ width: '20%', ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                    Individual Clients
                  </TableCell>
                  <TableCell align="center" sx={{ width: '20%', ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.map((stat) => (
                  <TableRow key={stat._id} sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
                    <TableCell align="center" sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, fontWeight: 600 }}>
                      {stat.propertyType}
                    </TableCell>
                    <TableCell align="center" sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                      {stat.sqft.toLocaleString()}
                    </TableCell>
                    <TableCell align="center" sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                      {stat.totalRequests.toLocaleString()}
                    </TableCell>
                    <TableCell align="center" sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT }}>
                      {(stat.individualClients || 0).toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleEditClick(stat)}>
                        <Icon name="Action3" color={COLORS.PRIMARY} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
          Edit {selectedStat?.propertyType}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Total SqFt"
              type="number"
              fullWidth
              value={editData.sqft}
              onChange={(e) => setEditData({ ...editData, sqft: e.target.value === '' ? '' : Number(e.target.value) })}
              InputProps={{
                sx: { fontFamily: FONT_FAMILY }
              }}
            />
            <TextField
              label="Total Requests"
              type="number"
              fullWidth
              value={editData.totalRequests}
              onChange={(e) => setEditData({ ...editData, totalRequests: e.target.value === '' ? '' : Number(e.target.value) })}
              InputProps={{
                sx: { fontFamily: FONT_FAMILY }
              }}
            />
            <TextField
              label="Individual Clients (Dummy Count)"
              type="number"
              fullWidth
              value={editData.individualClients}
              onChange={(e) => setEditData({ ...editData, individualClients: e.target.value === '' ? '' : Number(e.target.value) })}
              InputProps={{
                sx: { fontFamily: FONT_FAMILY }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setEditModalOpen(false)} sx={{ color: COLORS.MUTED_TEXT, textTransform: "none" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={saving}
            sx={{ backgroundColor: COLORS.PRIMARY, color: "#FFF", textTransform: "none", "&:hover": { backgroundColor: COLORS.SECONDARY } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editGlobalModalOpen} onClose={() => setEditGlobalModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
          Edit Global Dummy Additions
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              label="Total Individual Clients"
              type="number"
              fullWidth
              value={editGlobalData.totalIndividualClients}
              onChange={(e) => setEditGlobalData({ ...editGlobalData, totalIndividualClients: e.target.value === '' ? '' : Number(e.target.value) })}
              InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
            />
            <TextField
              label="Total Requests"
              type="number"
              fullWidth
              value={editGlobalData.totalRequests}
              onChange={(e) => setEditGlobalData({ ...editGlobalData, totalRequests: e.target.value === '' ? '' : Number(e.target.value) })}
              InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
            />
            <TextField
              label="Total SqFt"
              type="number"
              fullWidth
              value={editGlobalData.totalSqft}
              onChange={(e) => setEditGlobalData({ ...editGlobalData, totalSqft: e.target.value === '' ? '' : Number(e.target.value) })}
              InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
            />
            <TextField
              label="Admin Added Projects"
              type="number"
              fullWidth
              value={editGlobalData.adminAddedProjects}
              onChange={(e) => setEditGlobalData({ ...editGlobalData, adminAddedProjects: e.target.value === '' ? '' : Number(e.target.value) })}
              InputProps={{ sx: { fontFamily: FONT_FAMILY } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={() => setEditGlobalModalOpen(false)} sx={{ color: COLORS.MUTED_TEXT, textTransform: "none" }}>
            Cancel
          </Button>
          <Button 
            onClick={handleGlobalSave} 
            variant="contained" 
            disabled={saving}
            sx={{ backgroundColor: COLORS.PRIMARY, color: "#FFF", textTransform: "none", "&:hover": { backgroundColor: COLORS.SECONDARY } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageDummyStats;
